import { useEffect, useMemo } from 'react'

import {
  Box,
  Card as MuiCard,
  CardHeader,
  Divider,
  IconButton,
  styled,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import PopupIssueTimeline from './PopupIssueTimeline'
import PopupSelectedIssue from './PopupSelectedIssue'
import SelectionChip from './SelectionChip'

import { useDatasetsContext } from '../DatasetsContext'
import { useDataNavigatorControlsContext } from '../DataNavigatorControlsContext'

import PointSymbol from '../../../common/primitives/PointSymbol'
import config from '../../../config'

import { colors } from '../../../utils/colorUtils'
import {
  getSelectedFeatureSet,
  getSelectedFeature,
} from '../../../utils/selectionUtils'

const Card = styled(MuiCard)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  top: config.mapAppBarHeight,
  marginLeft: theme.spacing(1.5),
  marginRight: theme.spacing(1.5),
  marginTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  paddingTop: theme.spacing(1.5),
  backgroundColor: colors.awaOffWhite,
  borderRadius: '10px',
  maxHeight: 'calc(100vh - 180px)',
  maxWidth: '350px',
  zIndex: 1000,
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    left: 0,
  },
}))

const Header = styled(CardHeader)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.common.white,
  textAlign: 'center',
  paddingTop: theme.spacing(1.5),
  paddingLeft: theme.spacing(3),
  paddingBottom: theme.spacing(1),
  borderBottom: `${theme.spacing(1)} solid ${theme.palette.secondary.dark}`,
}))

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
}))

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  float: 'left',
}))

const Description = styled('p')(({ theme }) => ({
  marginBottom: 0,
  marginTop: 0,
  color: theme.palette.text.primary,
}))

const isInside = (innerRect, outerRect) =>
  innerRect.top > outerRect.top && innerRect.bottom < outerRect.bottom

const SelectionPopup = ({ featureSets, expandedGroup }) => {
  const { data } = useDatasetsContext()
  const { 
    clickFeatureOnDataPanel,
    selectedFeatures, 
    selectedKey, 
    setSelectedFeatures, 
    setSelectedKey 
  } = useDataNavigatorControlsContext()

  // Filter selected features to only show those from the expanded group
  const filteredSelectedFeatures = useMemo(
    () => selectedFeatures.filter((sf) => sf.featureSet === expandedGroup),
    [selectedFeatures, expandedGroup]
  )

  const selectedFeatureSet = useMemo(
    () => getSelectedFeatureSet(featureSets, filteredSelectedFeatures),
    [featureSets, filteredSelectedFeatures]
  )

  useEffect(() => {
    if (selectedKey) {
      const contentBoxElement = document.querySelector('#content-box')
      const selectedIssueElement = document.querySelector(
        `#popup-${selectedKey}`
      )
      if (
        contentBoxElement &&
        selectedIssueElement &&
        !isInside(
          selectedIssueElement.getBoundingClientRect(),
          contentBoxElement.getBoundingClientRect()
        )
      ) {
        selectedIssueElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
  }, [selectedKey])

  if (!filteredSelectedFeatures || filteredSelectedFeatures.length === 0 || !selectedFeatureSet) {
    return null
  }

  const issuesForFeature = (featureName) =>
    data.filter((issue) => issue.model === featureName && issue.modelType === selectedFeatureSet.displayName)
  const selectedIssue = data.find((issue) => issue.key === selectedKey) || {}

  const title = (
    <>
      <PointSymbol
        color={selectedFeatureSet.color}
        sx={{ float: 'left', mr: 1 }}
      />
      <Title>{selectedFeatureSet.popupConfig.title}</Title>
    </>
  )

  const header = (
    <Header
      title={title}
      action={
        <CloseButton onClick={() => setSelectedFeatures([])}>
          <CloseIcon />
        </CloseButton>
      }
    />
  )

  const chips = filteredSelectedFeatures.length > 0 && (
    <div style={{ marginTop: '8px' }}>
      {filteredSelectedFeatures.map((featureSelection, idx) => {
        const featureSet = featureSets.find(
          (fs) => fs.name === featureSelection.featureSet
        )
        const feature = featureSet?.featureCollection?.features.find(
          (f) => f.properties.FID === featureSelection.featureId
        )
        const featureName = feature?.properties[featureSet?.classProperty]
        
        return (
          <SelectionChip
            key={idx}
            featureSelection={featureSelection}
            featureSets={featureSets}
            href={`#popup-${idx}`}
            style={{ display: 'inline-block' }}
            onClick={(e) => {
              e.preventDefault()
              const element = document.querySelector(`#popup-${idx}`)
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
              }
            }}
            onDelete={() =>
              clickFeatureOnDataPanel(
                featureSelection.featureSet,
                featureName,
                featureSelection.featureId
              )
            }
          />
        )
      })}
    </div>
  )

  const intro = (
    <Box
      sx={{
        fontSize: '0.8rem',
        pb: 1,
        pt: 2,
        px: 4,
        textAlign: 'center',
        maxHeight: '200px',
        overflowY: 'auto',
        flexShrink: 0,
      }}
    >
      <Description>{selectedFeatureSet.popupConfig.description}</Description>
      {chips}
    </Box>
  )

  const divider = (
    <Divider
      sx={{
        borderBottomWidth: '2px',
        borderColor: 'secondary.light',
        mx: 4,
        my: 0.5,
      }}
    />
  )

  const content = (
    <Box id="content-box" sx={{ mx: 1.5, my: 0, overflowY: 'auto', flex: 1 }}>
      {filteredSelectedFeatures.map((feature, i) => {
        const selectedFeature = getSelectedFeature(
          [feature],
          selectedFeatureSet
        )
        if (!selectedFeature) return null

        return (
          <Box
            id={`popup-${i}`}
            key={i}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              position: 'relative',
              px: 2,
              pt: 2 * (i !== 0),
            }}
          >
            <div>
              <span
                style={{
                  backgroundColor: colors.awaOffWhite + 'FF',
                  fontSize: '0.8rem',
                  paddingBottom: '10px',
                  paddingTop: '10px',
                  position: 'sticky',
                  textTransform: 'uppercase',
                  top: 0,
                }}
              >
                <b>
                  {selectedFeatureSet.popupConfig.propMap.Model(
                    selectedFeature.properties
                  )}
                </b>
              </span>
              <p style={{ fontSize: '0.7rem' }}>
                Area -
                <b>
                  {' '}
                  {selectedFeatureSet.popupConfig.propMap.Area(
                    selectedFeature.properties
                  )}
                </b>
              </p>
            </div>
            <PopupIssueTimeline
              issuesForModel={issuesForFeature(
                selectedFeature.properties[selectedFeatureSet.classProperty]
              )}
              selectedKey={selectedKey}
              setSelectedKey={setSelectedKey}
            />
          </Box>
        )
      })}
    </Box>
  )

  const details = (
    <Box sx={{ alignSelf: 'flex-end', px: 4, py: 1, width: '100%' }}>
      <Typography fontSize="0.8rem" fontWeight={600} sx={{ mb: 0.5 }}>
        Dataset Details -
      </Typography>
      <PopupSelectedIssue selectedIssue={{ ...selectedIssue }} />
    </Box>
  )

  return (
    <Card style={{ display: 'flex', flexDirection: 'column' }}>
      {header}
      {intro}
      {divider}
      {content}
      {divider}
      {details}
    </Card>
  )
}

export default SelectionPopup
